import { jsx, jsxs } from "react/jsx-runtime";
import React__default, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import { FormOnColor, AnalyticsId, AVERAGE_CHARACTER_WIDTH_PX } from "./constants.js";
import { useUuid } from "./hooks/useUuid.js";
import { getAriaDescribedBy } from "./utils/accessibility.js";
import { u as uuid } from "./uuid.js";
import { E as ErrorWrapper } from "./ErrorWrapper.js";
import { a as renderLabel } from "./Label.js";
import { M as MaxCharacters } from "./MaxCharacters.js";
import styles from "./components/Textarea/styles.module.scss";
const getTextareaMaxWidth = (characters) => {
  const paddingWidth = "2rem";
  const scrollbarWidth = "16px";
  const borderWidth = "4px";
  return `calc(${characters * AVERAGE_CHARACTER_WIDTH_PX}px + ${paddingWidth} + ${scrollbarWidth} + ${borderWidth})`;
};
const Textarea = React__default.forwardRef((props, ref) => {
  const {
    maxCharacters,
    maxText,
    width,
    testId,
    defaultValue,
    marginBottom: gutterBottom,
    transparent,
    onColor = FormOnColor.onwhite,
    label,
    textareaId = uuid(),
    minRows = 3,
    maxRows = 10,
    grow,
    error,
    errorText,
    errorTextId,
    errorWrapperClassName,
    autoFocus,
    disabled,
    name,
    autoComplete = "off",
    placeholder,
    readOnly,
    required,
    onChange,
    value,
    ...rest
  } = props;
  const [rows, setRows] = useState(minRows);
  const [textareaInput, setTextareaInput] = useState(value || defaultValue || "");
  const referanse = useRef(null);
  const errorTextUuid = useUuid(errorTextId);
  useEffect(() => {
    setTextareaInput(defaultValue || "");
  }, [defaultValue]);
  const resizeHeight = (target) => {
    const textareaLineHeight = 28;
    const previousRows = target.rows;
    target.rows = minRows;
    const currentRows = Math.floor((target.scrollHeight - 16) / textareaLineHeight);
    if (currentRows === previousRows) {
      target.rows = currentRows;
    }
    if (currentRows >= maxRows) {
      target.rows = maxRows;
      target.scrollTop = target.scrollHeight;
    }
    if (currentRows < maxRows) {
      setRows(currentRows);
    } else {
      setRows(maxRows);
    }
  };
  const onDark = onColor === FormOnColor.ondark;
  const onBlueberry = onColor === FormOnColor.onblueberry;
  const maxCharactersExceeded = !!maxCharacters && textareaInput.toString().length > maxCharacters;
  const onError = onColor === FormOnColor.oninvalid || !!errorText || !!error || maxCharactersExceeded;
  const textareaWrapperClass = classNames(styles.textarea, {
    [styles["textarea--gutterBottom"]]: gutterBottom
  });
  const contentWrapperClass = classNames(styles["input-container"], {
    [styles["input-container--transparent"]]: transparent,
    [styles["input-container--on-blueberry"]]: onBlueberry,
    [styles["input-container--on-dark"]]: onDark,
    [styles["input-container--invalid"]]: onError,
    [styles["input-container--disabled"]]: props.disabled
  });
  const textareaClass = classNames(styles["input-container__input"], {
    [styles[`input-container__input--disabled`]]: props.disabled
  });
  useEffect(() => {
    var _a, _b, _c;
    value && setTextareaInput(value);
    if (grow && ((_a = referanse.current) == null ? void 0 : _a.children) && ((_b = referanse.current) == null ? void 0 : _b.children[0])) {
      const textarea = (_c = referanse.current) == null ? void 0 : _c.children[0];
      resizeHeight(textarea);
    }
  }, [value]);
  const onChangeHandler = (e) => {
    setTextareaInput(e.target.value);
    if (onChange) {
      onChange(e);
    }
    if (grow) {
      resizeHeight(e.target);
    }
  };
  const maxWidth = width ? getTextareaMaxWidth(width) : void 0;
  return /* @__PURE__ */ jsx(ErrorWrapper, { className: errorWrapperClassName, errorText, errorTextId: errorTextUuid, children: /* @__PURE__ */ jsxs("div", { "data-testid": testId, "data-analyticsid": AnalyticsId.Textarea, className: textareaWrapperClass, children: [
    renderLabel(label, textareaId, onColor),
    /* @__PURE__ */ jsx("div", { className: contentWrapperClass, ref: referanse, style: { maxWidth }, children: /* @__PURE__ */ jsx(
      "textarea",
      {
        ...rest,
        rows,
        defaultValue,
        id: textareaId,
        className: textareaClass,
        ref,
        "aria-describedby": getAriaDescribedBy(props, errorTextUuid),
        "aria-invalid": !!onError,
        autoFocus,
        disabled,
        name,
        autoComplete: autoComplete ? autoComplete : void 0,
        placeholder,
        readOnly,
        required,
        onChange: onChangeHandler,
        value
      }
    ) }),
    maxCharacters && /* @__PURE__ */ jsx(
      MaxCharacters,
      {
        maxCharacters,
        length: textareaInput.toString().length,
        maxText,
        onColor,
        maxWidth
      }
    )
  ] }) });
});
Textarea.displayName = "Textarea";
export {
  Textarea as T
};
//# sourceMappingURL=Textarea.js.map
